import { useCallback, useEffect } from 'react';

import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { forgetShapes } from 'skullet';

import { AUTH_TOKEN_KEY } from '@/constant/localStorage';
import { auth, completeLogin } from '@/services/auth';
import { passkeys } from '@/services/passkeys';
import xano from '@/services/xano';
import {
  ChangePasswordData,
  CreateCompanyRegistrationData,
  JoinCompanyData,
  RegisterData,
  SetupCompanyBcIntegrationRequest,
  UpdateProfileData,
  User,
} from '@/types/auth';
import storage from '@/utils/storage';

const AUTH_STALE_TIME = 30000; // 30 seconds
const AUTH_GC_TIME = 5 * 60 * 1000; // 5 minutes

export const AUTH_QUERY_OPTIONS = {
  staleTime: AUTH_STALE_TIME,
  gcTime: AUTH_GC_TIME,
};

const USER_QUERY_KEY = 'auth_user';

const userQueryOptions = queryOptions<User | null | undefined>({
  queryKey: [USER_QUERY_KEY],
  queryFn: () => auth.me(),
  ...AUTH_QUERY_OPTIONS,
});

export function useAuth() {
  const logout = useLogout();

  const {
    data: user,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    ...userQueryOptions,
    enabled: auth.isAuthenticated(),
  });

  useEffect(() => {
    if (error) {
      logout();
    }
  }, [error, logout]);

  useEffect(() => {
    if (user) {
      const token = storage.getItem(AUTH_TOKEN_KEY);
      xano.setRealtimeAuthToken(token);
    }
  }, [user]);

  return {
    user,
    isLoading,
    isError,
    error,
    refetch,
  };
}

export function useUser() {
  const { user, ...queryData } = useAuth();

  return {
    user: user!,
    ...queryData,
  };
}

export function useLogout() {
  const queryClient = useQueryClient();

  const logout = useCallback(() => {
    // Skullet keeps traced card shapes in localStorage. Drop them so the next
    // person on a shared device does not inherit this session's layout.
    forgetShapes();
    auth.logout();
    queryClient.setQueryData(userQueryOptions.queryKey, null);

    /**
     * Timeout, to not refetch all queries immediately, since it will cause 401.
     * After 100ms user will definitely be redirected to login page, so no refetches will occur.
     * */
    setTimeout(() => {
      queryClient.invalidateQueries();
    }, 100);
  }, [queryClient]);

  return logout;
}

export function useLogin() {
  const queryClient = useQueryClient();

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      auth.login(email, password),
    onSuccess: user => {
      queryClient.setQueryData(userQueryOptions.queryKey, user);
    },
  });

  return loginMutation;
}

/**
 * Sign in with a passkey. Ends in the same completeLogin step as password sign-in, then sets the
 * User the same way useLogin does. `autofill` waits for the User to pick a passkey in the email field.
 */
export function usePasskeyLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ autofill = false }: { autofill?: boolean } = {}) =>
      completeLogin(await passkeys.login({ autofill })),
    onSuccess: user => {
      queryClient.setQueryData(userQueryOptions.queryKey, user);
    },
  });
}

export function useSignup() {
  const queryClient = useQueryClient();

  const signupMutation = useMutation({
    mutationFn: ({
      firstName,
      lastName,
      email,
      password,
      phone,
    }: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
      phone: string;
    }) => auth.signup(firstName, lastName, email, password, phone),
    onSuccess: user => {
      queryClient.setQueryData(userQueryOptions.queryKey, user);
    },
  });

  return signupMutation;
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  const updateProfileMutation = useMutation({
    mutationFn: (profileData: Partial<UpdateProfileData>) =>
      auth.updateProfile(profileData),
    onSuccess: updatedUser => {
      queryClient.setQueryData(userQueryOptions.queryKey, prevUser => ({
        ...prevUser!,
        ...updatedUser,
      }));
    },
  });

  return updateProfileMutation;
}

export function useChangePassword() {
  const changePasswordMutation = useMutation({
    mutationFn: (data: ChangePasswordData) => auth.changePassword(data),
  });

  return changePasswordMutation;
}

export function useVerifyToken() {
  const verifyTokenMutation = useMutation({
    mutationFn: (token: string) => auth.verifyToken(token),
  });

  return verifyTokenMutation;
}

export function useJoinCompany() {
  const queryClient = useQueryClient();

  const joinCompanyMutation = useMutation({
    mutationFn: ({
      token,
      firstName,
      lastName,
      email,
      password,
      phone,
    }: JoinCompanyData) =>
      auth.joinCompany({ token, firstName, lastName, email, password, phone }),
    onSuccess: user => {
      queryClient.setQueryData(userQueryOptions.queryKey, user);
    },
  });

  return joinCompanyMutation;
}

export function useGoogleAuthUrl(redirectUrl: string) {
  const googleAuthUrlQuery = useQuery({
    queryKey: ['google_auth_url', redirectUrl],
    queryFn: () => auth.getGoogleAuthUrl(redirectUrl),
    enabled: !!redirectUrl,
  });

  return googleAuthUrlQuery;
}

export function useCreateCompanyRegistration() {
  const queryClient = useQueryClient();

  const createCompanyRegistrationMutation = useMutation({
    mutationFn: (params: CreateCompanyRegistrationData) =>
      auth.createCompanyRegistration(params),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: companyRegistrationQueryOptions.queryKey,
      });
    },
  });

  return createCompanyRegistrationMutation;
}

const companyRegistrationQueryOptions = queryOptions({
  queryKey: ['company_registration'],
  queryFn: () => auth.getCompanyRegistration(),
  ...AUTH_QUERY_OPTIONS,
  refetchOnMount: false,
  retryOnMount: false,
});

export function useGetCompanyRegistration(enabled = true) {
  const getCompanyRegistrationQuery = useQuery({
    ...companyRegistrationQueryOptions,
    enabled,
  });

  return getCompanyRegistrationQuery;
}

export function useRegister() {
  const registerMutation = useMutation({
    mutationFn: (params: RegisterData) => auth.register(params),
  });

  return registerMutation;
}

export function useSetupCompanyBcIntegration() {
  const queryClient = useQueryClient();

  const setupCompanyBcIntegrationMutation = useMutation({
    mutationFn: (params: SetupCompanyBcIntegrationRequest) =>
      auth.setupCompanyBcIntegration(params),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: companyRegistrationQueryOptions.queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: ['company'],
        }),
      ]);
    },
  });

  return setupCompanyBcIntegrationMutation;
}

export function useVerifyEmail(token: string | null, enabled = true) {
  const queryClient = useQueryClient();

  const verifyEmailQuery = useQuery({
    queryKey: ['verify_email', token],
    queryFn: () => auth.verifyEmail(token!),
    enabled: Boolean(token) && enabled,
    retry: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (verifyEmailQuery.data) {
      queryClient.setQueryData(
        userQueryOptions.queryKey,
        verifyEmailQuery.data
      );
    }
  }, [queryClient, verifyEmailQuery.data]);

  return verifyEmailQuery;
}

export function useSendForgotPassword() {
  const sendForgotPasswordMutation = useMutation({
    mutationFn: (email: string) => auth.sendForgotPassword(email),
  });

  return sendForgotPasswordMutation;
}

export function useResetPassword() {
  const resetPasswordMutation = useMutation({
    mutationFn: (params: { token: string; password: string }) =>
      auth.resetPassword(params),
  });

  return resetPasswordMutation;
}
